import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiSearch, FiMail, FiPhone, FiInfo, FiUserPlus } from "react-icons/fi";
import { fetchContacts, fetchCategoryStats, updateContactStatus } from "../api/contacts";
import ContactDetailsModal from "../components/ContactDetailsModal";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";

const LEAD_STATUS_CONFIG = {
  new: { label: "New Lead", bg: "bg-blue-50 text-blue-700 border-blue-200" },
  emailed: { label: "Emailed", bg: "bg-gray-100 text-gray-700 border-gray-200" },
  replied: { label: "Replied 🔥", bg: "bg-amber-50 text-amber-800 border-amber-300 font-semibold" },
  meeting_set: { label: "Meeting Set 📅", bg: "bg-purple-50 text-purple-800 border-purple-300 font-semibold" },
  won: { label: "Client Won 🎉", bg: "bg-emerald-50 text-emerald-800 border-emerald-300 font-bold" },
  not_interested: { label: "Not Interested", bg: "bg-red-50 text-red-700 border-red-200" },
};

export default function Contacts() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [contacts, setContacts] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const [categories, setCategories] = useState([]);
  const [categorySearch, setCategorySearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [emailSentFilter, setEmailSentFilter] = useState(""); // '', 'True', 'False'
  const [leadStatusFilter, setLeadStatusFilter] = useState("");

  const [selectedId, setSelectedId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const { token } = useAuth();
  const categoryInputRef = useRef(null);

  // 🔹 Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  // 🔹 Fetch categories
  useEffect(() => {
    const loadCategories = async () => {
      if (!token?.access) return;
      try {
        const data = await fetchCategoryStats(token.access);
        setCategories(data || []);
      } catch (err) {
        console.error("Failed to fetch categories:", err.message);
      }
    };
    loadCategories();
  }, [token]);

  // 🔹 Fetch contacts
  useEffect(() => {
    const loadContacts = async () => {
      if (!token?.access) return;
      setLoading(true);
      try {
        const data = await fetchContacts(
          token.access,
          page,
          pageSize,
          debouncedSearch,
          selectedCategory,
          emailSentFilter,
          leadStatusFilter
        );
        setContacts(data.results);
        setTotalPages(data.total_pages);
        setTotalCount(data.count);
      } catch (error) {
        console.error("Failed to load contacts:", error.message);
      } finally {
        setLoading(false);
      }
    };
    loadContacts();
  }, [page, token, debouncedSearch, selectedCategory, pageSize, emailSentFilter, leadStatusFilter]);

  // 🔹 Update lead status
  const handleStatusChange = async (contactId, newStatus) => {
    try {
      await updateContactStatus(token.access, contactId, newStatus);
      setContacts((prev) =>
        prev.map((c) => (c.id === contactId ? { ...c, lead_status: newStatus } : c))
      );
      toast.success("Lead status updated!");
    } catch (err) {
      toast.error(err.message || "Failed to update status");
    }
  };

  // 🔹 Selection Handlers
  const toggleSelectRow = (id) => {
    const updated = new Set(selectedIds);
    if (updated.has(id)) {
      updated.delete(id);
    } else {
      updated.add(id);
    }
    setSelectedIds(updated);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === contacts.length && contacts.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(contacts.map((c) => c.id)));
    }
  };

  const handleSendToSelected = () => {
    navigate("/dashboard/email/send", {
      state: { selectedContactIds: Array.from(selectedIds) },
    });
  };

  // 🔹 Filtered categories
  const filteredCategories = categories.filter((cat) =>
    cat.category.toLowerCase().includes(categorySearch.toLowerCase())
  );

  // 🔹 Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        categoryInputRef.current &&
        !categoryInputRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Smart pagination logic
  const getVisiblePages = () => {
    const totalVisible = 5;
    const pages = [];
    if (totalPages <= totalVisible) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, page + 2);
    if (page <= 3) end = 5;
    if (page >= totalPages - 2) start = totalPages - 4;
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const visiblePages = getVisiblePages();

  return (
    <div className="p-6">
      {/* Header + Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-gray-800">
            Contacts ({totalCount})
          </h2>
          <Link
            to="/dashboard/contacts/add"
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-3.5 py-2 rounded-lg text-sm flex items-center gap-2 transition shadow-sm"
          >
            <FiUserPlus size={18} /> Add Contact
          </Link>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          {/* Contact Search */}
          <div className="relative w-full sm:w-72">
            <FiSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search contacts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>

          {/* Category Filter */}
          <div className="relative w-full sm:w-64" ref={categoryInputRef}>
            <input
              type="text"
              placeholder="Search category..."
              value={categorySearch}
              onChange={(e) => {
                setCategorySearch(e.target.value);
                setDropdownOpen(true);
              }}
              onFocus={() => setDropdownOpen(true)}
              className="w-full pl-3 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
            {dropdownOpen && (
              <div className="absolute z-10 mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto w-full">
                {filteredCategories.length > 0 ? (
                  filteredCategories.map((cat) => (
                    <div
                      key={cat.category}
                      onClick={() => {
                        setSelectedCategory(cat.category);
                        setCategorySearch("");
                        setDropdownOpen(false);
                        setPage(1);
                      }}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                    >
                      {cat.category} ({cat.count})
                    </div>
                  ))
                ) : (
                  <div className="px-3 py-2 text-gray-500">No match</div>
                )}
              </div>
            )}
          </div>

          {selectedCategory && (
            <button
              onClick={() => setSelectedCategory("")}
              className="px-3 py-2 bg-gray-200 rounded-lg text-sm hover:bg-gray-300"
            >
              Clear: {selectedCategory}
            </button>
          )}

          {/* Email Sent Filter */}
          <select
            value={emailSentFilter}
            onChange={(e) => {
              setEmailSentFilter(e.target.value);
              setPage(1);
            }}
            className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
          >
            <option value="">All Emails</option>
            <option value="True">Email Sent</option>
            <option value="False">Not Sent</option>
          </select>

          {/* Lead Status Filter */}
          <select
            value={leadStatusFilter}
            onChange={(e) => {
              setLeadStatusFilter(e.target.value);
              setPage(1);
            }}
            className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
          >
            <option value="">All Lead Stages</option>
            <option value="new">New Lead</option>
            <option value="emailed">Emailed</option>
            <option value="replied">Replied 🔥</option>
            <option value="meeting_set">Meeting Set 📅</option>
            <option value="won">Client Won 🎉</option>
            <option value="not_interested">Not Interested</option>
          </select>
        </div>
      </div>

      {/* Helpful Hint Banner */}
      <div className="bg-blue-50/80 border border-blue-200 rounded-lg p-3 mb-4 flex items-center gap-2 text-xs text-blue-900 shadow-sm">
        <span className="text-base shrink-0">💡</span>
        <span>
          <strong>Freelance Lead Pipeline Tip:</strong> Click any contact's <strong>Lead Status</strong> badge to update their status as you communicate (e.g. <em>New Lead → Emailed → Replied 🔥 → Client Won 🎉</em>).
        </span>
      </div>

      {/* Loading */}
      {loading && (
        <p className="text-center text-gray-500">Loading contacts...</p>
      )}

      {/* Contacts Table */}
      <div className="overflow-x-auto bg-white rounded-xl shadow border max-h-[600px] overflow-y-auto">
        <table className="min-w-full text-sm text-left text-gray-700">
          <thead className="bg-gray-100 text-gray-700 uppercase text-xs sticky top-0 z-10">
            <tr>
              <th className="px-3 py-3 w-10 text-center">
                <input
                  type="checkbox"
                  checked={contacts.length > 0 && selectedIds.size === contacts.length}
                  onChange={toggleSelectAll}
                  className="rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                  title="Select All on Page"
                />
              </th>
              <th className="px-4 py-3">#</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Company</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Phone</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Area</th>
              <th className="px-4 py-3 whitespace-nowrap">Lead Stage</th>
              <th className="px-4 py-3 whitespace-nowrap">Email Status</th>
              <th className="px-4 py-3">Unsubscribed</th>
              <th className="px-4 py-3">Bounced</th>
              <th className="px-4 py-3">Complaint</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && contacts.length > 0 ? (
              contacts.map((contact) => {
                const isSelected = selectedIds.has(contact.id);
                return (
                  <tr
                    key={contact.id}
                    className={`border-t transition ${isSelected ? "bg-blue-50/60" : "hover:bg-gray-50"}`}
                  >
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelectRow(contact.id)}
                        className="rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-0">{contact.id}</td>
                  <td className="px-4 py-1 font-medium text-gray-900 whitespace-nowrap truncate max-w-[250px]">
                    {contact.name}
                  </td>
                  <td className="px-4 py-0 whitespace-nowrap truncate max-w-[250px]">
                    {contact.company_name || "-"}
                  </td>
                  <td className="px-4 py-0">
                    <div className="flex items-center gap-2">
                      <FiMail className="text-gray-500" />
                      <span>{contact.email}</span>
                    </div>
                  </td>
                  <td className="px-4 py-0">
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      <FiPhone className="text-gray-500" />
                      <span>{contact.phone || "-"}</span>
                    </div>
                  </td>
                  <td className="px-4 py-0 whitespace-nowrap">
                    {contact.category || "Uncategorized"}
                  </td>
                  <td className="px-4 py-0 whitespace-nowrap truncate max-w-[250px]">
                    {contact.area || "-"}
                  </td>
                  {/* Lead Status Dropdown Badge */}
                  <td className="px-4 py-1 whitespace-nowrap">
                    {(() => {
                      const stKey = contact.lead_status || "new";
                      const conf = LEAD_STATUS_CONFIG[stKey] || LEAD_STATUS_CONFIG["new"];
                      return (
                        <select
                          value={stKey}
                          onChange={(e) => handleStatusChange(contact.id, e.target.value)}
                          className={`text-xs font-semibold px-2.5 py-1 rounded-full border cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400 ${conf.bg}`}
                          title="Click to update lead status"
                        >
                          <option value="new">New Lead</option>
                          <option value="emailed">Emailed</option>
                          <option value="replied">Replied 🔥</option>
                          <option value="meeting_set">Meeting Set 📅</option>
                          <option value="won">Client Won 🎉</option>
                          <option value="not_interested">Not Interested</option>
                        </select>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-0 whitespace-nowrap">
                    {contact.email_status || "-"}
                  </td>
                  <td className="px-4 py-0 whitespace-nowrap">
                    {contact.unsubscribed ? "Yes" : "-"}
                  </td>
                  <td className="px-4 py-0 whitespace-nowrap">
                    {contact.bounced ? "Yes" : "-"}
                  </td>
                  <td className="px-4 py-0 whitespace-nowrap">
                    {contact.complaint ? "Yes" : "-"}
                  </td>
                  <td className="px-4 py-0 text-right">
                    <button
                      onClick={() => {
                        setSelectedId(contact.id);
                        setModalOpen(true);
                      }}
                      className="p-2 rounded-md text-blue-600 hover:bg-blue-100 transition"
                      title="View Details"
                    >
                      <FiInfo size={16} />
                    </button>
                  </td>
                </tr>
              );
            })
            ) : (
              <tr>
                <td
                  colSpan="12"
                  className="px-4 py-6 text-center text-gray-500 italic"
                >
                  {loading ? "Loading contacts..." : "No contacts found"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination & Page Size Selector */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between mt-6 gap-3">
          <div className="flex items-center gap-2 text-sm">
            <span>Rows per page:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="border rounded-lg px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            >
              {[10, 25, 50, 100, 500, 1000].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          <nav className="flex items-center gap-1 text-sm">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50"
            >
              Prev
            </button>

            {visiblePages[0] > 1 && (
              <>
                <button
                  onClick={() => setPage(1)}
                  className="px-3 py-1 border rounded-lg hover:bg-gray-100"
                >
                  1
                </button>
                <span className="px-2 text-gray-500">...</span>
              </>
            )}

            {visiblePages.map((num) => (
              <button
                key={num}
                onClick={() => setPage(num)}
                className={`px-3 py-1 border rounded-lg ${
                  page === num
                    ? "bg-blue-600 text-white"
                    : "hover:bg-gray-100 text-gray-700"
                }`}
              >
                {num}
              </button>
            ))}

            {visiblePages[visiblePages.length - 1] < totalPages && (
              <>
                <span className="px-2 text-gray-500">...</span>
                <button
                  onClick={() => setPage(totalPages)}
                  className="px-3 py-1 border rounded-lg hover:bg-gray-100"
                >
                  {totalPages}
                </button>
              </>
            )}

            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50"
            >
              Next
            </button>
          </nav>
        </div>
      )}

      {/* Contact Details Modal */}
      <ContactDetailsModal
        contactId={selectedId}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
      />

      {/* Floating Selected Actions Bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 right-6 z-40 bg-gray-900 text-white px-5 py-3.5 rounded-xl shadow-2xl flex items-center gap-4 border border-gray-700">
          <span className="text-xs font-semibold">
            <strong className="text-blue-400 text-sm">{selectedIds.size}</strong> Contact{selectedIds.size === 1 ? "" : "s"} Selected
          </span>
          <button
            onClick={handleSendToSelected}
            className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2 transition shadow-md"
          >
            <FiMail size={16} /> Send Email to Selected
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-gray-400 hover:text-white text-xs underline"
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
}
